import {
  useEventdetectorApi,
  useFetchAllEventdetectorDefinitions,
} from '@ais-ui/api';
import { useToast } from '@ais-ui/ui-core';
import { useEffect, useMemo, useRef } from 'react';
import { Session } from '@ais-ui/auth';
import { usePermissions } from '../Permissions/PermissionsProvider';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettings } from '../Settings/SettingsProvider';
import { useNotificationSound } from '../utils/useNotificationSound';
import { getCurrentEvents, useRefetchOnSocketChange } from '@ais-ui/websockets';
import { useQuery } from '@tanstack/react-query';
import { parseEventSeverity } from './eventSeverity';
import { dispatchFocusEvent } from '../Events/focusEvent';

interface EventDetail {
  event_ids: string[];
}

// Event toasts carry a "View event" action, so give users extra time to read
// and click before the toast auto-dismisses (Sonner's default is 4s).
const EVENT_TOAST_DURATION_MS = 8000;

/**
 * Failed event IDs are remembered so a broken event is neither re-fetched nor
 * re-toasted on every subsequent notification. Left unbounded the set grows
 * precisely during a backend outage - the moment the app can least afford it,
 * and the same conditions that produce a long reconnect storm.
 *
 * Insertion order makes the oldest ID the safest to forget: it has long since
 * stopped arriving in notifications, and the worst case if it ever returns is
 * one extra failed fetch and one extra toast.
 */
export const MAX_REMEMBERED_FAILED_EVENT_IDS = 500;

export function rememberFailedEventId(
  failedEventIds: Set<string>,
  eventId: string,
): void {
  failedEventIds.add(eventId);

  if (failedEventIds.size <= MAX_REMEMBERED_FAILED_EVENT_IDS) {
    return;
  }

  const oldestEventId = failedEventIds.values().next().value;
  if (oldestEventId !== undefined) {
    failedEventIds.delete(oldestEventId);
  }
}

function severityToToastVariant(severity: string) {
  switch (severity) {
    case 'alert':
      return 'destructive';
    case 'warning':
      return 'warning';
    default:
      return 'default';
  }
}

export const useEventNotification = (olSession: Session) => {
  const eventDetectorApi = useEventdetectorApi();
  const permissions = usePermissions();
  const { messages } = useLocalization();
  const { settings } = useSettings();
  const { playNotificationSound } = useNotificationSound();

  const soundAlertLevels = useMemo(() => {
    return {
      note:
        settings['ribbonPlugin.settings']?.sound_alert_levels?.note ?? false,
      warning:
        settings['ribbonPlugin.settings']?.sound_alert_levels?.warning ?? false,
      alert:
        settings['ribbonPlugin.settings']?.sound_alert_levels?.alert ?? false,
    };
  }, [settings]);

  const { data: detectors } = useFetchAllEventdetectorDefinitions(
    permissions.events,
  );
  const { toast } = useToast();

  // Use a ref to track failed event IDs across renders
  const failedEventIdsRef = useRef<Set<string>>(new Set());

  // --- WebSocket-based notifications (when backend sends 'update' messages) ---
  useEffect(() => {
    const handleEventNotification = async (event: Event) => {
      const customEvent = event as CustomEvent<EventDetail>;

      for (const eventId of customEvent.detail.event_ids) {
        if (!eventId) {
          console.error('Event ID of event notification is null or undefined');
          continue;
        }

        // Skip processing if we've already failed on this event ID
        if (failedEventIdsRef.current.has(eventId)) {
          continue;
        }

        if (!detectors || !detectors.records) continue;

        try {
          // Fetch the event using the eventid from the eventNotification
          const response = await eventDetectorApi.getEventDetails({
            eventId: eventId,
          });

          const fetchedEvent = response.records?.[0];

          if (!fetchedEvent) {
            console.warn(`No event details found for event ID: ${eventId}`);
            continue;
          }

          // Get the id of the detector that triggered the event
          const detectorId = fetchedEvent.definition?.id;
          if (!detectorId) {
            console.warn(`No detector ID found for event ID: ${eventId}`);
            continue;
          }

          // Go through all detectors of the user to find the detector with the right id
          const detector = detectors.records.find(
            (det) => det.id === detectorId,
          );

          // Get event details for notification
          const backendSeverity: string | undefined = fetchedEvent.event
            ?.severity as string;

          const severity = parseEventSeverity(backendSeverity);
          const variant = severityToToastVariant(severity);
          const event = fetchedEvent.event;
          const ship = event?.ship;
          const drone = event?.drone;
          const shipName = drone ? 'Drone' : ship?.name || 'Unknown ship';

          const detectorName = detector?.name ?? 'Unknown detector';

          const toastTitle = 'New event from ' + detectorName;
          const toastDescription = 'Triggered by ' + shipName;

          toast({
            variant: variant,
            title: toastTitle,
            description: toastDescription,
            duration: EVENT_TOAST_DURATION_MS,
            action: {
              label: messages.eventNotificationViewAction,
              onClick: () => dispatchFocusEvent(eventId),
            },
          });

          // Play sound if enabled for the specific event severity level
          if (
            (severity === 'note' && soundAlertLevels.note) ||
            (severity === 'warning' && soundAlertLevels.warning) ||
            (severity === 'alert' && soundAlertLevels.alert)
          ) {
            await playNotificationSound();
          }
        } catch (error) {
          // Handle API errors
          console.error(
            `Error fetching event details for event ID: ${eventId}`,
            error,
          );

          // Add this event ID to our set of failed events
          rememberFailedEventId(failedEventIdsRef.current, eventId);

          // Show error toast to user - only once per event ID
          toast({
            variant: 'destructive',
            title: messages.genericToastTitleError,
            description: `Could not retrieve details for event with ID ${eventId}.`,
          });

          continue;
        }
      }
    };

    window.addEventListener('eventNotification', handleEventNotification);
    return () => {
      window.removeEventListener('eventNotification', handleEventNotification);
    };
  }, [
    detectors,
    eventDetectorApi,
    soundAlertLevels,
    messages,
    olSession,
    toast,
    playNotificationSound,
  ]);

  // --- Polling-based notifications (fallback when WebSocket push is unavailable) ---
  // Keyed on the stable `sessionId`, not the per-reconnect `socketId`, so a
  // reconnect reuses one cache entry; `useRefetchOnSocketChange` still refetches
  // against the new socket.
  const isPollingEnabled = !!olSession && !!permissions?.events;
  const { data: currentEventsData, refetch: refetchCurrentEvents } = useQuery({
    queryKey: ['eventNotificationPoll', olSession?.sessionId],
    queryFn: () => getCurrentEvents(olSession),
    refetchInterval: 15_000,
    staleTime: 0,
    enabled: isPollingEnabled,
  });
  useRefetchOnSocketChange(
    olSession?.socketId,
    isPollingEnabled,
    refetchCurrentEvents,
  );
  const knownEventIdsRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    const currentEvents = currentEventsData?.events;
    if (!currentEvents) return;

    const currentIds = new Set(currentEvents.map((e) => e.id));

    // First fetch — seed known IDs without toasting
    if (knownEventIdsRef.current === null) {
      knownEventIdsRef.current = currentIds;
      return;
    }

    for (const event of currentEvents) {
      if (knownEventIdsRef.current.has(event.id)) continue;

      const severity = parseEventSeverity(event.severity);
      const variant = severityToToastVariant(severity);

      toast({
        variant: variant,
        title: 'New event: ' + (event.name || 'Unknown'),
        description: 'Triggered by ' + (event.shipName || 'Unknown ship'),
        duration: EVENT_TOAST_DURATION_MS,
        action: {
          label: messages.eventNotificationViewAction,
          onClick: () => dispatchFocusEvent(event.id),
        },
      });

      if (
        (severity === 'note' && soundAlertLevels.note) ||
        (severity === 'warning' && soundAlertLevels.warning) ||
        (severity === 'alert' && soundAlertLevels.alert)
      ) {
        playNotificationSound();
      }
    }

    knownEventIdsRef.current = currentIds;
  }, [
    currentEventsData,
    toast,
    soundAlertLevels,
    playNotificationSound,
    messages,
  ]);
};
